import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Target, CalendarDays, MessageCircle, Music2, Youtube, Menu, X, HeartHandshake, Lock } from "lucide-react";
import { externalLinks } from "../mock";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Programs & Pro Shop", to: "/programs" },
  { label: "Contact", to: "/contact" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group">
      <span className="relative flex h-10 w-10 items-center justify-center rounded-md bg-[#ff2e4d] shadow-[0_0_20px_-4px_rgba(255,46,77,0.7)]">
        <Target className="h-6 w-6 text-white" strokeWidth={2.4} />
      </span>
      <span className="leading-none">
        <span className="block font-condensed text-xl text-white tracking-wide">BULLS ON PARADE</span>
        <span className="block text-[10px] uppercase tracking-[0.28em] text-[#ff2e4d] font-semibold">Ontario Darts Hub</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const iconLinks = [
    { icon: CalendarDays, to: "/tournament-calendars", external: false, label: "Tournament calendars" },
    { icon: MessageCircle, to: externalLinks.discord, external: true, label: "Community chat" },
    { icon: Music2, to: externalLinks.spotify, external: true, label: "Spotify" },
    { icon: Youtube, to: externalLinks.youtube, external: true, label: "YouTube" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-[#0c0c0e]/90 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      {/* GoFundMe strip */}
      <a
        href={externalLinks.gofundme}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 bg-[#ff2e4d] py-1.5 text-xs sm:text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors"
      >
        <HeartHandshake className="h-4 w-4" />
        Support the program on GoFundMe
      </a>

      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 py-3">
        <Logo />

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`bop-link-underline text-sm font-medium transition-colors ${
                pathname === item.to ? "text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-1.5">
          {iconLinks.map(({ icon: Icon, to, external, label }) =>
            external ? (
              <a
                key={label}
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-md text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Icon className="h-5 w-5" />
              </a>
            ) : (
              <Link
                key={label}
                to={to}
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-md text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Icon className="h-5 w-5" />
              </Link>
            )
          )}
          <Link
            to="/admin"
            aria-label="Admin"
            className="ml-1 inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-sm font-semibold text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Lock className="h-4 w-4" /> Admin
          </Link>
        </div>

        <button
          className="md:hidden flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-white/10"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0c0c0e] border-t border-white/10 px-4 py-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`block rounded-md px-3 py-2.5 text-sm font-medium ${
                pathname === item.to ? "bg-white/10 text-white" : "text-zinc-300 hover:bg-white/5"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/admin"
            className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium ${
              pathname === "/admin" ? "bg-white/10 text-white" : "text-zinc-300 hover:bg-white/5"
            }`}
          >
            <Lock className="h-4 w-4" /> Admin
          </Link>
          <div className="flex items-center gap-2 pt-3">
            {iconLinks.map(({ icon: Icon, to, external, label }) =>
              external ? (
                <a key={label} href={to} target="_blank" rel="noopener noreferrer" aria-label={label} className="flex h-10 w-10 items-center justify-center rounded-md bg-white/5 text-zinc-300">
                  <Icon className="h-5 w-5" />
                </a>
              ) : (
                <Link key={label} to={to} aria-label={label} className="flex h-10 w-10 items-center justify-center rounded-md bg-white/5 text-zinc-300">
                  <Icon className="h-5 w-5" />
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
